import { throttle } from "lodash";
import React, { useEffect, useRef } from "react";
import { SESSION_STORAGE_KEY } from "../../../utils/session-storage";

interface IdleTimerProps {
  onIdle: () => void;
  timeout?: number;
}

const IdleTimer: React.FC<IdleTimerProps> = ({ onIdle, timeout }) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);

  const resetTimer = () => {
    if (!mountedRef.current) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(onIdle, timeout);
    sessionStorage.setItem(SESSION_STORAGE_KEY.LAST_ACTIVITY_TIME, Date.now().toString());
  };

  const throttledResetTimer = useRef(throttle(resetTimer, 500)).current;

  useEffect(() => {
    const lastActivityTime = Number(sessionStorage.getItem(SESSION_STORAGE_KEY.LAST_ACTIVITY_TIME));
    const currentTime = Date.now();
    if (lastActivityTime && currentTime - lastActivityTime > (timeout || 15 * 60 * 1000)) {
      onIdle();
    }
    throttledResetTimer();

    window.addEventListener("mousemove", throttledResetTimer);
    window.addEventListener("keydown", throttledResetTimer);
    window.addEventListener("click", throttledResetTimer);
    window.addEventListener("scroll", throttledResetTimer);
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      window.removeEventListener("mousemove", throttledResetTimer);
      window.removeEventListener("keydown", throttledResetTimer);
      window.removeEventListener("click", throttledResetTimer);
      window.removeEventListener("scroll", throttledResetTimer);
    };
  }, [onIdle, timeout]);

  return null;
};

export default IdleTimer;
